import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaInstagram } from 'react-icons/fa';

const serviceLinks = ['Roofing', 'Plumbing', 'Bathrooms', 'Framing', 'Windows', 'Electricity'];

const Footer = () => {
  return (
    <FooterContainer>
      <FooterTop>
        <FooterGrid>
          <BrandCol>
            <FooterBrandName>Champion &amp; Champion</FooterBrandName>
            <BrandTagline>
              Transforming Spaces,<br />Creating Legacies
            </BrandTagline>
            <SocialRow>
              <SocialLink
                href="https://www.instagram.com/championandchampion/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
              >
                <FaInstagram />
              </SocialLink>
            </SocialRow>
          </BrandCol>

          <ServicesCol>
            <ColHeading>Our Services</ColHeading>
            <NavList>
              {serviceLinks.map(s => (
                <NavItem key={s}>
                  <FooterLink to={`/${s.toLowerCase()}`}>{s}</FooterLink>
                </NavItem>
              ))}
            </NavList>
          </ServicesCol>

          <ContactCol>
            <ColHeading>Get In Touch</ColHeading>
            <ContactList>
              <ContactRow>
                <FaPhoneAlt style={{ color: '#c89432', flexShrink: 0 }} />
                <ContactLink href="tel:+18188095041">1 (818) 809-5041</ContactLink>
              </ContactRow>
              <ContactRow>
                <FaEnvelope style={{ color: '#c89432', flexShrink: 0 }} />
                <ContactLink href="mailto:championbuilding12@gmail.com">
                  championbuilding12@gmail.com
                </ContactLink>
              </ContactRow>
              <ContactRow>
                <FaMapMarkerAlt style={{ color: '#c89432', flexShrink: 0 }} />
                <ContactText>Los Angeles, California</ContactText>
              </ContactRow>
            </ContactList>
            <SpanishNote>¡Hablamos Español!</SpanishNote>
          </ContactCol>
        </FooterGrid>
      </FooterTop>

      <FooterBottom>
        <Copyright>© 2026 Champion &amp; Champion Construction. All rights reserved.</Copyright>
        <BottomLinks>
          <BottomLink href="/">Home</BottomLink>
          <BottomLink href="/contact">Contact</BottomLink>
          <BottomLink href="/privacy-policy">Privacy Policy</BottomLink>
        </BottomLinks>
      </FooterBottom>
      <DevCredit>
        Website developed by{' '}
        <DevLink href="https://pulsecraftweb.com" target="_blank" rel="noopener noreferrer">
          PulseCraftWeb
        </DevLink>
      </DevCredit>
    </FooterContainer>
  );
};

const FooterContainer = styled.footer`
  background: #0d1117;
  color: #e5e7eb;
  font-family: "Poppins", sans-serif;
`;

const FooterTop = styled.div`
  padding: 72px 32px 56px;
  @media (max-width: 768px) {
    padding: 52px 20px 40px;
  }
`;

const FooterGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr;
  gap: 48px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const BrandCol = styled.div``;

const FooterBrandName = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
`;

const BrandTagline = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  line-height: 1.7;
  margin-bottom: 24px;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #1f2937;
  color: #9ca3af;
  font-size: 1.1rem;
  transition: background 0.25s ease, color 0.25s ease;
  &:hover {
    background: #c89432;
    color: #fff;
  }
`;

const ServicesCol = styled.div``;

const ContactCol = styled.div``;

const ColHeading = styled.h4`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #c89432;
  margin-bottom: 20px;
`;

const NavList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NavItem = styled.li``;

const FooterLink = styled(Link)`
  font-size: 0.9rem;
  color: #9ca3af;
  transition: color 0.2s ease;
  &:hover {
    color: #e5e7eb;
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-top: 2px;
`;

const ContactLink = styled.a`
  font-size: 0.875rem;
  color: #9ca3af;
  line-height: 1.5;
  transition: color 0.2s ease;
  &:hover {
    color: #e5e7eb;
  }
`;

const ContactText = styled.span`
  font-size: 0.875rem;
  color: #9ca3af;
  line-height: 1.5;
`;

const SpanishNote = styled.p`
  font-size: 0.8rem;
  font-style: italic;
  color: #6b7280;
  margin-top: 4px;
`;

const FooterBottom = styled.div`
  border-top: 1px solid #1f2937;
  padding: 20px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  max-width: 100%;
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    padding: 20px;
  }
`;

const Copyright = styled.p`
  font-size: 0.8rem;
  color: #4b5563;
`;

const BottomLinks = styled.div`
  display: flex;
  gap: 20px;
`;

const BottomLink = styled.a`
  font-size: 0.8rem;
  color: #4b5563;
  transition: color 0.2s ease;
  &:hover {
    color: #9ca3af;
  }
`;

const DevCredit = styled.div`
  text-align: center;
  padding: 10px 32px 16px;
  font-family: "Poppins", sans-serif;
  font-size: 0.75rem;
  color: #374151;
`;

const DevLink = styled.a`
  color: #c89432;
  font-weight: 500;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.75;
  }
`;

export default Footer;
